/**
 * useParticleSystem Hook - GPU-driven particle system
 * Manages procedural particles that respond to panels, cursors, and user activity
 */

import { useRef, useEffect, useMemo, useCallback } from 'react';
import * as THREE from 'three';
import { createParticleMaterial, FLOOR_PALETTE } from './living-floor-materials';

// Enable debug logging by setting localStorage.setItem('LIVING_FLOOR_DEBUG', 'true')
const DEBUG =
  typeof window !== 'undefined' && localStorage.getItem('LIVING_FLOOR_DEBUG') === 'true';

export interface ParticleSystemConfig {
  count?: number;
  speed?: number;
  repulsion?: number;
  attraction?: number;
  friction?: number;
  boundsX?: number;
  boundsZ?: number;
  particleSize?: number;
}

export interface ParticleSystemState {
  geometry: THREE.BufferGeometry;
  material: THREE.PointsMaterial;
  update: (data: ParticleUpdateData) => void;
  reset: () => void;
  dispose: () => void;
}

export interface ParticleUpdateData {
  panelPositions?: Array<{ x: number; y: number; z: number }>;
  cursorPos?: THREE.Vector3;
  activeUsers?: Array<{ x: number; y: number; z: number; intensity: number }>;
  time?: number;
  /** Audio amplitude 0-1, scales particle size and velocity when present */
  audioAmplitude?: number;
}

const DEFAULT_CONFIG: Required<ParticleSystemConfig> = {
  count: 50000,
  speed: 0.1,
  repulsion: 0.5,
  attraction: 0.02,
  friction: 0.95,
  boundsX: 100,
  boundsZ: 100,
  particleSize: 0.5, // Increased for better visibility
};

// Inline color helper — writes directly into colors array to avoid per-particle allocation.
// Left (x<0): violet/indigo → Center: cyan-white → Right (x>0): teal
function writeParticleColor(
  colors: Float32Array,
  pIdx: number,
  x: number,
  y: number,
  boundsX: number
): void {
  const t = (x + boundsX * 0.5) / boundsX; // 0=left, 1=right
  const yT = y > 0 ? Math.min(y / 15, 1.0) : 0;
  const brightness = 0.25 + yT * 0.75;
  let r: number, g: number, b: number;
  if (t < 0.5) {
    const s = t * 2;
    r = (0.33 - 0.08 * s) * brightness;
    g = 0.85 * s * brightness;
    b = brightness;
  } else {
    const s = (t - 0.5) * 2;
    r = 0.25 * (1 - s) * brightness;
    g = (0.85 - 0.1 * s) * brightness;
    b = (1.0 - 0.05 * s) * brightness;
  }
  colors[pIdx] = r;
  colors[pIdx + 1] = g;
  colors[pIdx + 2] = b;
}

/**
 * Hook to manage GPU particle system
 * Handles initialization, update, and cleanup of particle geometry/material
 */
export const useParticleSystem = (config: ParticleSystemConfig = {}): ParticleSystemState => {
  const mergedConfig = { ...DEFAULT_CONFIG, ...config };

  // Refs for particle data
  const geometryRef = useRef<THREE.BufferGeometry | null>(null);
  const materialRef = useRef<THREE.PointsMaterial | null>(null);
  const positionsRef = useRef<Float32Array | null>(null);
  const velocitiesRef = useRef<Float32Array | null>(null);
  const agesRef = useRef<Float32Array | null>(null);
  const colorsRef = useRef<Float32Array | null>(null);
  const isMagentaRef = useRef<Uint8Array | null>(null); // tracks which particles are magenta accents
  const maxAgeRef = useRef<number>(300); // 5 seconds at 60fps
  const initializedRef = useRef<boolean>(false);
  const updateCountRef = useRef<number>(0);

  // Initialize particle geometry synchronously to ensure it's available immediately
  if (!initializedRef.current) {
    try {
      const count = mergedConfig.count;

      // Create geometry
      const geometry = new THREE.BufferGeometry();

      // Initialize positions (random within bounds)
      const positions = new Float32Array(count * 3);
      const velocities = new Float32Array(count * 3);
      const ages = new Float32Array(count);
      const colors = new Float32Array(count * 3);
      const isMagenta = new Uint8Array(count);

      for (let i = 0; i < count; i++) {
        // Random position on floor - distributed in visible area
        positions[i * 3] = (Math.random() - 0.5) * mergedConfig.boundsX;
        positions[i * 3 + 1] = Math.random() * 15; // Distribute from 0 to 15 (above floor)
        positions[i * 3 + 2] = (Math.random() - 0.5) * mergedConfig.boundsZ;

        // Random initial velocity
        velocities[i * 3] = (Math.random() - 0.5) * mergedConfig.speed;
        velocities[i * 3 + 1] = Math.random() * mergedConfig.speed * 0.3;
        velocities[i * 3 + 2] = (Math.random() - 0.5) * mergedConfig.speed;

        // Age of particle (for fade out)
        ages[i] = Math.random() * maxAgeRef.current;

        // ~3% magenta accent particles — spectral outliers
        isMagenta[i] = Math.random() < 0.03 ? 1 : 0;

        // Initial per-particle color
        const ci = i * 3;
        if (isMagenta[i]) {
          const yT = Math.min(positions[ci + 1] / 15, 1.0);
          const brightness = 0.3 + yT * 0.7;
          colors[ci] = 0.85 * brightness;
          colors[ci + 1] = 0.0;
          colors[ci + 2] = 0.95 * brightness;
        } else {
          writeParticleColor(colors, ci, positions[ci], positions[ci + 1], mergedConfig.boundsX);
        }
      }

      // Set attributes
      geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      geometry.setAttribute('velocity', new THREE.BufferAttribute(velocities, 3));
      geometry.setAttribute('age', new THREE.BufferAttribute(ages, 1));
      geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

      geometryRef.current = geometry;
      positionsRef.current = positions;
      velocitiesRef.current = velocities;
      agesRef.current = ages;
      colorsRef.current = colors;
      isMagentaRef.current = isMagenta;

      // Create material with vertex colors for bioluminescent gradient
      const material = createParticleMaterial({
        size: mergedConfig.particleSize,
        vertexColors: true,
      });
      materialRef.current = material;

      initializedRef.current = true;
      if (DEBUG) {
        console.log('[useParticleSystem] Initialized with', mergedConfig.count, 'particles');
      }
    } catch (error) {
      console.error('[useParticleSystem] Error initializing:', error);
    }
  }

  // Update particles based on forces
  const updateParticles = useCallback(
    (data: ParticleUpdateData) => {
      if (!geometryRef.current || !positionsRef.current || !velocitiesRef.current) {
        console.warn('[useParticleSystem] Missing geometry/positions/velocities', {
          hasGeometry: !!geometryRef.current,
          hasPositions: !!positionsRef.current,
          hasVelocities: !!velocitiesRef.current,
        });
        return;
      }

      const positions = positionsRef.current;
      const velocities = velocitiesRef.current;
      const ages = agesRef.current!;
      const colors = colorsRef.current!;
      const isMagenta = isMagentaRef.current!;
      const count = mergedConfig.count;

      const panelPositions = data.panelPositions || [];
      const cursorPos = data.cursorPos || new THREE.Vector3(0, 0, 0);
      // Only attract to cursor when it has actually moved from origin
      const hasCursor =
        cursorPos.x !== 0 || cursorPos.y !== 0 || cursorPos.z !== 0;
      // Audio reactivity — scales velocity and particle spread
      const audioAmp = data.audioAmplitude ?? 0;
      // Update colors every 3 frames — changes are gradual, imperceptible to throttle
      const updateColors = updateCountRef.current % 3 === 0;

      // Track update count for debugging (disabled by default - causes spam)
      updateCountRef.current++;
      if (DEBUG && updateCountRef.current % 300 === 0) {
        console.log(
          '[useParticleSystem] Update #',
          updateCountRef.current,
          'particles:',
          count,
          'panels:',
          panelPositions.length
        );
      }

      // Update each particle
      for (let i = 0; i < count; i++) {
        const pIdx = i * 3;

        let forceX = 0;
        let forceY = 0;
        let forceZ = 0;

        // Repulsion from panels
        for (const panel of panelPositions) {
          const dx = positions[pIdx] - panel.x;
          const dy = positions[pIdx + 1] - panel.y;
          const dz = positions[pIdx + 2] - panel.z;
          const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);

          if (dist < 20) {
            const repel = mergedConfig.repulsion / Math.max(dist, 1);
            forceX += (dx / dist) * repel;
            forceY += (dy / dist) * repel;
            forceZ += (dz / dist) * repel;
          }
        }

        // Attraction to cursor
        const cursorDx = cursorPos.x - positions[pIdx];
        const cursorDy = cursorPos.y - positions[pIdx + 1];
        const cursorDz = cursorPos.z - positions[pIdx + 2];
        const cursorDist = Math.sqrt(
          cursorDx * cursorDx + cursorDy * cursorDy + cursorDz * cursorDz
        );

        if (hasCursor && cursorDist < 30 && cursorDist > 1) {
          const attract = mergedConfig.attraction / (1 + cursorDist * 0.1);
          forceX += (cursorDx / cursorDist) * attract;
          forceY += (cursorDy / cursorDist) * attract;
          forceZ += (cursorDz / cursorDist) * attract;
        }

        // Apply forces to velocity
        velocities[pIdx] += forceX;
        velocities[pIdx + 1] += forceY;
        velocities[pIdx + 2] += forceZ;

        // Audio reactivity — boost velocity outward when audio is present
        if (audioAmp > 0.05) {
          const audioPush = audioAmp * 0.15;
          velocities[pIdx] += (Math.random() - 0.5) * audioPush;
          velocities[pIdx + 1] += Math.random() * audioPush * 0.5;
          velocities[pIdx + 2] += (Math.random() - 0.5) * audioPush;
        }

        // Apply friction
        velocities[pIdx] *= mergedConfig.friction;
        velocities[pIdx + 1] *= mergedConfig.friction;
        velocities[pIdx + 2] *= mergedConfig.friction;

        // Gravity
        velocities[pIdx + 1] -= 0.01;

        // Update position
        positions[pIdx] += velocities[pIdx];
        positions[pIdx + 1] += velocities[pIdx + 1];
        positions[pIdx + 2] += velocities[pIdx + 2];

        // Boundary wrapping
        if (positions[pIdx] > mergedConfig.boundsX / 2) {
          positions[pIdx] = -mergedConfig.boundsX / 2;
        }
        if (positions[pIdx] < -mergedConfig.boundsX / 2) {
          positions[pIdx] = mergedConfig.boundsX / 2;
        }

        if (positions[pIdx + 2] > mergedConfig.boundsZ / 2) {
          positions[pIdx + 2] = -mergedConfig.boundsZ / 2;
        }
        if (positions[pIdx + 2] < -mergedConfig.boundsZ / 2) {
          positions[pIdx + 2] = mergedConfig.boundsZ / 2;
        }

        // Age particle
        ages[i]++;
        if (ages[i] > maxAgeRef.current) {
          ages[i] = 0;
          // Reset to random position
          positions[pIdx] = (Math.random() - 0.5) * mergedConfig.boundsX;
          positions[pIdx + 1] = Math.random() * 15; // Redistribute 0 to 15 above floor
          positions[pIdx + 2] = (Math.random() - 0.5) * mergedConfig.boundsZ;
          velocities[pIdx] = 0;
          velocities[pIdx + 1] = 0;
          velocities[pIdx + 2] = 0;
        }

        // Update color every 3 frames — changes are slow, throttle saves GC
        if (updateColors) {
          if (isMagenta[i]) {
            const yT = Math.min(positions[pIdx + 1] / 15, 1.0);
            const brightness = 0.3 + yT * 0.7;
            colors[pIdx] = 0.85 * brightness;
            colors[pIdx + 1] = 0.0;
            colors[pIdx + 2] = 0.95 * brightness;
          } else {
            writeParticleColor(colors, pIdx, positions[pIdx], positions[pIdx + 1], mergedConfig.boundsX);
          }
        }
      }

      // Audio-reactive particle size — pulse with amplitude
      if (materialRef.current && audioAmp > 0.05) {
        materialRef.current.size = mergedConfig.particleSize * (1.0 + audioAmp * 1.5);
      } else if (materialRef.current) {
        // Smoothly return to base size
        materialRef.current.size += (mergedConfig.particleSize - materialRef.current.size) * 0.1;
      }

      // Mark geometry for update
      const posAttr = geometryRef.current.getAttribute('position') as THREE.BufferAttribute;
      posAttr.needsUpdate = true;

      const ageAttr = geometryRef.current.getAttribute('age') as THREE.BufferAttribute;
      ageAttr.needsUpdate = true;

      if (updateColors) {
        const colorAttr = geometryRef.current.getAttribute('color') as THREE.BufferAttribute;
        colorAttr.needsUpdate = true;
      }
    },
    [mergedConfig.count, mergedConfig.boundsX, mergedConfig.boundsZ]
  );

  const reset = useCallback(() => {
    if (!positionsRef.current || !velocitiesRef.current) return;

    const positions = positionsRef.current;
    const velocities = velocitiesRef.current;
    const colors = colorsRef.current!;
    const isMagenta = isMagentaRef.current!;
    const count = mergedConfig.count;

    for (let i = 0; i < count; i++) {
      positions[i * 3] = (Math.random() - 0.5) * mergedConfig.boundsX;
      positions[i * 3 + 1] = Math.random() * 15; // Distribute 0 to 15 above floor
      positions[i * 3 + 2] = (Math.random() - 0.5) * mergedConfig.boundsZ;

      velocities[i * 3] = 0;
      velocities[i * 3 + 1] = 0;
      velocities[i * 3 + 2] = 0;

      const ci = i * 3;
      if (isMagenta[i]) {
        const yT = Math.min(positions[ci + 1] / 15, 1.0);
        const brightness = 0.3 + yT * 0.7;
        colors[ci] = 0.85 * brightness;
        colors[ci + 1] = 0.0;
        colors[ci + 2] = 0.95 * brightness;
      } else {
        writeParticleColor(colors, ci, positions[ci], positions[ci + 1], mergedConfig.boundsX);
      }
    }

    if (geometryRef.current) {
      const posAttr = geometryRef.current.getAttribute('position') as THREE.BufferAttribute;
      posAttr.needsUpdate = true;
      const colorAttr = geometryRef.current.getAttribute('color') as THREE.BufferAttribute;
      colorAttr.needsUpdate = true;
    }
  }, [mergedConfig.count, mergedConfig.boundsX, mergedConfig.boundsZ]);

  const dispose = useCallback(() => {
    if (geometryRef.current) {
      geometryRef.current.dispose();
      geometryRef.current = null;
    }
    if (materialRef.current) {
      materialRef.current.dispose();
      materialRef.current = null;
    }
  }, []);

  // Return the particle system state
  // Note: These should always be non-null after synchronous initialization above
  const result = useMemo(
    () => ({
      geometry: geometryRef.current!,
      material: materialRef.current!,
      update: updateParticles,
      reset,
      dispose,
    }),
    [updateParticles, reset, dispose]
  );

  return result;
};
